import { Injectable, computed, effect, signal } from '@angular/core';
import {
  DraftAssignment,
  DraftItem,
  PublicBillSplitDiscountDraft,
  DistributionMode,
  TipType,
  DiscountType,
  DraftParticipant,
  SplitOriginSnapshot,
  DraftAdjustmentDistribution,
} from '@features/client/models/public-bill-split-models';

@Injectable({ providedIn: 'root' })
export class PublicBillSplitDraftStore {
  private readonly STORAGE_KEYS = {
    splitId: 'dinno_split_id',
    step: 'dinno_split_step',
    items: 'dinno_draft_items',
    participants: 'dinno_draft_participants',
    assignments: 'dinno_draft_assignments',
    discounts: 'dinno_draft_discounts',
    discountDistributions: 'dinno_draft_discount_distributions',
    preview: 'dinno_split_preview',
    lastUpdate: 'dinno_last_update',
    ticketImageUrl: 'dinno_ticket_image_url',
    processingId: 'dinno_last_processing_id',
    qrVisibleBySplit: 'dinno_host_qr_visible_by_split',
  } as const;

  readonly splitId = signal<string | null>(localStorage.getItem(this.STORAGE_KEYS.splitId));

  readonly step = signal<1 | 2 | 3 | 4>(
    this.loadNumber(this.STORAGE_KEYS.step, 1) as 1 | 2 | 3 | 4,
  );

  readonly title = signal('Mi cuenta');
  readonly currency = signal('ARS');

  readonly tipType = signal<TipType | null>(null);
  readonly tipValue = signal<number | null>(null);
  readonly tipDistributionMode = signal<DistributionMode>('PROPORTIONAL');
  readonly tipParticipantIds = signal<string[]>([]);
  readonly discountDistributions = signal<DraftAdjustmentDistribution[]>(
    this.loadJson(this.STORAGE_KEYS.discountDistributions, []),
  );

  readonly discountType = signal<DiscountType | null>(null);
  readonly discountValue = signal<number | null>(null);
  readonly discountDistributionMode = signal<DistributionMode>('PROPORTIONAL');

  readonly splitOrigins = signal<Record<string, SplitOriginSnapshot>>({});

  readonly participants = signal<DraftParticipant[]>(
    this.loadJson(this.STORAGE_KEYS.participants, []),
  );

  readonly items = signal<DraftItem[]>(this.loadJson(this.STORAGE_KEYS.items, []));

  readonly assignments = signal<DraftAssignment[]>(
    this.loadJson(this.STORAGE_KEYS.assignments, []),
  );

  readonly discounts = signal<PublicBillSplitDiscountDraft[]>(
    this.loadJson(this.STORAGE_KEYS.discounts, []),
  );

  readonly qrVisibleBySplit = signal<Record<string, boolean>>(
    this.loadJson(this.STORAGE_KEYS.qrVisibleBySplit, {}),
  );

  readonly itemsSubtotal = computed(() =>
    this.items().reduce((acc, item) => acc + item.totalPrice, 0),
  );

  readonly totalDiscountAmount = computed(() =>
    this.discounts().reduce((acc, discount) => acc + discount.amount, 0),
  );

  readonly netSubtotal = computed(() =>
    Math.max(0, this.itemsSubtotal() - this.totalDiscountAmount()),
  );

  constructor() {
    effect(() => {
      localStorage.setItem(this.STORAGE_KEYS.step, String(this.step()));
    });

    effect(() => {
      localStorage.setItem(this.STORAGE_KEYS.items, JSON.stringify(this.items()));
    });

    effect(() => {
      localStorage.setItem(this.STORAGE_KEYS.participants, JSON.stringify(this.participants()));
    });

    effect(() => {
      localStorage.setItem(this.STORAGE_KEYS.assignments, JSON.stringify(this.assignments()));
    });

    effect(() => {
      localStorage.setItem(this.STORAGE_KEYS.discounts, JSON.stringify(this.discounts()));
    });

    effect(() => {
      localStorage.setItem(
        this.STORAGE_KEYS.discountDistributions,
        JSON.stringify(this.discountDistributions()),
      );
    });

    effect(() => {
      localStorage.setItem(
        this.STORAGE_KEYS.qrVisibleBySplit,
        JSON.stringify(this.qrVisibleBySplit()),
      );
    });

    effect(() => {
      this.items();
      this.sanitizeAssignments();
    });

    this.sanitizeAssignments();
  }

  updateConfig(config: {
    title?: string;
    currency?: string;
    tipType?: TipType | null;
    tipValue?: number | null;
    tipDistributionMode?: DistributionMode;
    discountType?: DiscountType | null;
    discountValue?: number | null;
    discountDistributionMode?: DistributionMode;
  }): void {
    if (config.title !== undefined) this.title.set(config.title);
    if (config.currency !== undefined) this.currency.set(config.currency);
    if (config.tipType !== undefined) this.tipType.set(config.tipType);
    if (config.tipValue !== undefined) this.tipValue.set(config.tipValue);
    if (config.tipDistributionMode !== undefined) {
      this.tipDistributionMode.set(config.tipDistributionMode);
    }
    if (config.discountType !== undefined) {
      this.discountType.set(config.discountType);
    }
    if (config.discountValue !== undefined) {
      this.discountValue.set(config.discountValue);
    }
    if (config.discountDistributionMode !== undefined) {
      this.discountDistributionMode.set(config.discountDistributionMode);
    }
  }

  setItems(items: DraftItem[]): void {
    this.items.set(items);
  }

  setParticipants(participants: DraftParticipant[]): void {
    this.participants.set(participants);
  }

  setAssignments(assignments: DraftAssignment[]): void {
    this.assignments.set(assignments);
  }

  setDiscounts(discounts: PublicBillSplitDiscountDraft[]): void {
    this.discounts.set(discounts);

    const currentById = new Map(this.discountDistributions().map((dist) => [dist.id, dist]));

    const nextDistributions: DraftAdjustmentDistribution[] = discounts.map((discount) => {
      const existing = currentById.get(discount.id);

      if (existing) {
        return {
          ...existing,
          label: discount.description || existing.label || 'Descuento',
          amount: discount.amount,
          type: 'DISCOUNT',
          enabled: true,
        };
      }

      return {
        id: discount.id,
        label: discount.description || 'Descuento',
        amount: discount.amount,
        distributionMode: 'PROPORTIONAL',
        participantIds: [],
        type: 'DISCOUNT',
        enabled: true,
      };
    });

    this.discountDistributions.set(nextDistributions);
  }

  setSplitId(splitId: string | null): void {
    this.splitId.set(splitId);

    if (splitId) {
      localStorage.setItem(this.STORAGE_KEYS.splitId, splitId);
    } else {
      localStorage.removeItem(this.STORAGE_KEYS.splitId);
    }
  }

  setStep(step: 1 | 2 | 3 | 4): void {
    this.step.set(step);
  }

  setQrVisible(splitId: string, isVisible: boolean): void {
    this.qrVisibleBySplit.update((current) => ({
      ...current,
      [splitId]: isVisible,
    }));
  }

  setTipParticipantIds(ids: string[]): void {
    this.tipParticipantIds.set(ids);
  }

  setDiscountDistributions(value: DraftAdjustmentDistribution[]): void {
    this.discountDistributions.set(value);
  }

  ensureAdjustmentDistributionsForParticipants(participantIds: string[]): void {
    if (!participantIds.length) return;

    this.discountDistributions.update((distributions) =>
      distributions.map((distribution) => ({
        ...distribution,
        participantIds:
          distribution.participantIds.length > 0 ? distribution.participantIds : participantIds,
      })),
    );
  }

  getQrVisible(splitId: string): boolean | null {
    const value = this.qrVisibleBySplit()[splitId];
    return value ?? null;
  }

  reserveOriginalSplitItem(sourceItem: DraftItem, createdChildrenIds: string[]): void {
    const originalItemId = sourceItem.originalItemId ?? sourceItem.id;
    const originalIndex = this.items().findIndex((i) => i.id === sourceItem.id);

    this.splitOrigins.update((current) => ({
      ...current,
      [originalItemId]: {
        originalItemId,
        item: { ...sourceItem },
        createdChildrenIds,
        createdAt: Date.now(),
        originalIndex,
      },
    }));
  }

  restoreOriginalSplitItem(
    items: DraftItem[],
    originalItemId: string,
    snapshot: SplitOriginSnapshot,
  ): DraftItem[] {
    const filtered = items.filter((item) => item.originalItemId !== originalItemId);

    const childIds = snapshot.createdChildrenIds;

    this.assignments.update((assignments) =>
      assignments.filter((a) => !childIds.includes(a.itemId)),
    );

    return [...filtered, snapshot.item];
  }

  getSplitOrigin(originalItemId: string): SplitOriginSnapshot | undefined {
    return this.splitOrigins()[originalItemId];
  }

  clearSplitOrigin(originalItemId: string): void {
    this.splitOrigins.update((current) => {
      const next = { ...current };
      delete next[originalItemId];
      return next;
    });
  }

  clearQrVisible(splitId: string): void {
    this.qrVisibleBySplit.update((current) => {
      const updated = { ...current };
      delete updated[splitId];
      return updated;
    });
  }

  clear(): void {
    Object.values(this.STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));

    this.splitId.set(null);
    this.title.set('Mi cuenta');
    this.currency.set('ARS');
    this.tipType.set(null);
    this.tipValue.set(null);
    this.tipDistributionMode.set('PROPORTIONAL');
    this.discountType.set(null);
    this.discountValue.set(null);
    this.discountDistributionMode.set('PROPORTIONAL');
    this.discountDistributions.set([]);
    this.participants.set([]);
    this.items.set([]);
    this.assignments.set([]);
    this.discounts.set([]);
    this.qrVisibleBySplit.set({});
  }

  private loadJson<T>(key: string, fallback: T): T {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  private loadNumber(key: string, fallback: number): number {
    const raw = localStorage.getItem(key);
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private sanitizeAssignments(): void {
    const validItemIds = new Set(
      this.items().flatMap((item) => [item.id, item.backendId].filter(Boolean) as string[]),
    );

    const current = this.assignments();

    const sanitized = current.filter((assignment) => validItemIds.has(assignment.itemId));

    if (sanitized.length === current.length) {
      return;
    }

    this.assignments.set(sanitized);
  }
}
